import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

/**
 * Bilingual (English / Arabic) language context for the appointments app.
 * Mirrors the admin app's approach: a `t({ en, ar })` selector, plus setting
 * <html lang/dir> so the layout flips RTL for Arabic (flex row order + text
 * alignment flip automatically under dir=rtl; use logical `ms-*`/`me-*` and
 * `text-start`/`text-end` for the rest). The choice persists in localStorage.
 */

export type Lang = 'en' | 'ar';

interface LangContextValue {
  lang: Lang;
  dir: 'ltr' | 'rtl';
  setLang: (l: Lang) => void;
  toggle: () => void;
  t: <T>(opts: { en: T; ar: T }) => T;
}

const LangContext = createContext<LangContextValue | null>(null);
const STORAGE_KEY = 'pos:appointments:lang';

function loadInitial(): Lang {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'ar' ? 'ar' : 'en';
  } catch {
    return 'en';
  }
}

export function LanguageProvider({ children }: { children: ReactNode }): JSX.Element {
  const [lang, setLangState] = useState<Lang>(() => loadInitial());

  useEffect(() => {
    const html = document.documentElement;
    html.setAttribute('lang', lang);
    html.setAttribute('dir', lang === 'ar' ? 'rtl' : 'ltr');
    try { localStorage.setItem(STORAGE_KEY, lang); } catch { /* ignore */ }
  }, [lang]);

  const setLang = useCallback((l: Lang) => setLangState(l), []);
  const toggle = useCallback(() => setLangState((l) => (l === 'ar' ? 'en' : 'ar')), []);
  const t = useCallback(<T,>(opts: { en: T; ar: T }): T => (lang === 'ar' ? opts.ar : opts.en), [lang]);

  const value = useMemo<LangContextValue>(
    () => ({ lang, dir: lang === 'ar' ? 'rtl' : 'ltr', setLang, toggle, t }),
    [lang, setLang, toggle, t],
  );

  return <LangContext.Provider value={value}>{children}</LangContext.Provider>;
}

export function useLang(): LangContextValue {
  const ctx = useContext(LangContext);
  if (!ctx) throw new Error('useLang must be used inside <LanguageProvider>');
  return ctx;
}

/** Pick the localized name (Arabic falls back to English when name_ar is empty). */
export function localName(lang: Lang, name: string | null | undefined, name_ar: string | null | undefined): string {
  return ((lang === 'ar' ? name_ar || name : name) ?? '') as string;
}
